/**
 * Nearby helpers shared by both sides: how this device introduces itself, and
 * the four emoji both screens show before a bridge is handed over.
 *
 * The emoji come from both public keys of the exchange. Each device
 * computes them from the keys it actually holds; if anyone in between had
 * swapped a key, the two screens would show different emoji.
 *
 * The commitment that makes the comparison binding, and the indexes of the emoji, are made in `@/crypto` (`pairing.ts`);
 * this file has the table and the words. The owner first publishes only a commitment to its key (`commitOwnerKey`) and
 * shows the key after the other device has answered, so whoever stands in between has one try per offer the person
 * answers, one chance in 16 777 216 each. Two guards make that real: this page declines a new offer while any dialog is
 * open (one answered offer is one try), and the owner presses “They match” before the key is wrapped, since an
 * `accepted` from the server proves nothing.
 */

import { sasIndexes, type ExchangeFlow } from '@/crypto';
import { t, type MessageKey } from '@/i18n';

import { appShell } from './app';

/** “iPhone · Safari”, “Mac · Chrome”… from the browser's own description. */
export function describeDevice(userAgent: string): string {
  const device = userAgent.includes('iPhone')
    ? 'iPhone'
    : userAgent.includes('iPad')
      ? 'iPad'
      : userAgent.includes('Android')
        ? t('nearby.deviceAndroid')
        : /Macintosh|Mac OS X/.test(userAgent)
          ? 'Mac'
          : userAgent.includes('Windows')
            ? t('nearby.deviceWindows')
            : userAgent.includes('Linux')
              ? t('nearby.deviceLinux')
              : t('nearby.deviceOther');
  const browser = userAgent.includes('Edg/')
    ? 'Edge'
    : /Firefox\/|FxiOS/.test(userAgent)
      ? 'Firefox'
      : /Chrome\/|CriOS/.test(userAgent)
        ? 'Chrome'
        : userAgent.includes('Safari/')
          ? 'Safari'
          : t('nearby.browserOther');
  return t('nearby.deviceBrowser', { device, browser });
}

/** How this page introduces itself: the browser's description, or “iPhone · Bridge app” in the native app. */
export function thisDevice(): string {
  const shell = appShell();
  if (shell !== null) return t('nearby.deviceApp', { device: shell.platform === 'ios' ? 'iPhone' : shell.platform === 'ipados' ? 'iPad' : 'Mac' });
  return describeDevice(navigator.userAgent);
}

export interface Sas {
  readonly emoji: string;
  readonly name: string;
}

/** 64 emoji that look alike on every platform and are easy to name aloud. */
export const SAS_EMOJI: readonly Sas[] = [
  { emoji: '🐶', name: 'dog' }, { emoji: '🐱', name: 'cat' }, { emoji: '🦁', name: 'lion' }, { emoji: '🐴', name: 'horse' },
  { emoji: '🦄', name: 'unicorn' }, { emoji: '🐷', name: 'pig' }, { emoji: '🐘', name: 'elephant' }, { emoji: '🐰', name: 'rabbit' },
  { emoji: '🐼', name: 'panda' }, { emoji: '🐓', name: 'rooster' }, { emoji: '🐧', name: 'penguin' }, { emoji: '🐢', name: 'turtle' },
  { emoji: '🐟', name: 'fish' }, { emoji: '🐙', name: 'octopus' }, { emoji: '🦋', name: 'butterfly' }, { emoji: '🌷', name: 'tulip' },
  { emoji: '🌳', name: 'tree' }, { emoji: '🌵', name: 'cactus' }, { emoji: '🍄', name: 'mushroom' }, { emoji: '🌍', name: 'globe' },
  { emoji: '🌙', name: 'moon' }, { emoji: '☁️', name: 'cloud' }, { emoji: '🔥', name: 'fire' }, { emoji: '🍌', name: 'banana' },
  { emoji: '🍎', name: 'apple' }, { emoji: '🍓', name: 'strawberry' }, { emoji: '🌽', name: 'corn' }, { emoji: '🍕', name: 'pizza' },
  { emoji: '🎂', name: 'cake' }, { emoji: '❤️', name: 'heart' }, { emoji: '😀', name: 'smiley' }, { emoji: '🤖', name: 'robot' },
  { emoji: '🎩', name: 'hat' }, { emoji: '👓', name: 'glasses' }, { emoji: '🔧', name: 'spanner' }, { emoji: '🎅', name: 'Santa' },
  { emoji: '👍', name: 'thumbs up' }, { emoji: '☂️', name: 'umbrella' }, { emoji: '⌛', name: 'hourglass' }, { emoji: '⏰', name: 'clock' },
  { emoji: '🎁', name: 'gift' }, { emoji: '💡', name: 'light bulb' }, { emoji: '📕', name: 'book' }, { emoji: '✏️', name: 'pencil' },
  { emoji: '📎', name: 'paperclip' }, { emoji: '✂️', name: 'scissors' }, { emoji: '🔒', name: 'lock' }, { emoji: '🔑', name: 'key' },
  { emoji: '🔨', name: 'hammer' }, { emoji: '☎️', name: 'telephone' }, { emoji: '🏁', name: 'flag' }, { emoji: '🚂', name: 'train' },
  { emoji: '🚲', name: 'bicycle' }, { emoji: '✈️', name: 'aeroplane' }, { emoji: '🚀', name: 'rocket' }, { emoji: '🏆', name: 'trophy' },
  { emoji: '⚽', name: 'ball' }, { emoji: '🎸', name: 'guitar' }, { emoji: '🎺', name: 'trumpet' }, { emoji: '🔔', name: 'bell' },
  { emoji: '⚓', name: 'anchor' }, { emoji: '🎧', name: 'headphones' }, { emoji: '📁', name: 'folder' }, { emoji: '📌', name: 'pin' },
];

const EMOJI_NAMES: Readonly<Record<string, MessageKey>> = {
  dog: 'emoji.dog',
  cat: 'emoji.cat',
  lion: 'emoji.lion',
  horse: 'emoji.horse',
  unicorn: 'emoji.unicorn',
  pig: 'emoji.pig',
  elephant: 'emoji.elephant',
  rabbit: 'emoji.rabbit',
  panda: 'emoji.panda',
  rooster: 'emoji.rooster',
  penguin: 'emoji.penguin',
  turtle: 'emoji.turtle',
  fish: 'emoji.fish',
  octopus: 'emoji.octopus',
  butterfly: 'emoji.butterfly',
  tulip: 'emoji.tulip',
  tree: 'emoji.tree',
  cactus: 'emoji.cactus',
  mushroom: 'emoji.mushroom',
  globe: 'emoji.globe',
  moon: 'emoji.moon',
  cloud: 'emoji.cloud',
  fire: 'emoji.fire',
  banana: 'emoji.banana',
  apple: 'emoji.apple',
  strawberry: 'emoji.strawberry',
  corn: 'emoji.corn',
  pizza: 'emoji.pizza',
  cake: 'emoji.cake',
  heart: 'emoji.heart',
  smiley: 'emoji.smiley',
  robot: 'emoji.robot',
  hat: 'emoji.hat',
  glasses: 'emoji.glasses',
  spanner: 'emoji.spanner',
  Santa: 'emoji.santa',
  'thumbs up': 'emoji.thumbsUp',
  umbrella: 'emoji.umbrella',
  hourglass: 'emoji.hourglass',
  clock: 'emoji.clock',
  gift: 'emoji.gift',
  'light bulb': 'emoji.lightBulb',
  book: 'emoji.book',
  pencil: 'emoji.pencil',
  paperclip: 'emoji.paperclip',
  scissors: 'emoji.scissors',
  lock: 'emoji.lock',
  key: 'emoji.key',
  hammer: 'emoji.hammer',
  telephone: 'emoji.telephone',
  flag: 'emoji.flag',
  train: 'emoji.train',
  bicycle: 'emoji.bicycle',
  aeroplane: 'emoji.aeroplane',
  rocket: 'emoji.rocket',
  trophy: 'emoji.trophy',
  ball: 'emoji.ball',
  guitar: 'emoji.guitar',
  trumpet: 'emoji.trumpet',
  bell: 'emoji.bell',
  anchor: 'emoji.anchor',
  headphones: 'emoji.headphones',
  folder: 'emoji.folder',
  pin: 'emoji.pin',
  unknown: 'emoji.unknown',
};

/** What an emoji is called in the current language, for the screen reader and for saying it aloud. */
export function emojiName(sas: Sas): string {
  const key = EMOJI_NAMES[sas.name];
  return key === undefined ? sas.name : t(key);
}

/** The four emoji for a pair of public keys: 24 bits of SHA-256 over both, six bits each. */
export async function sasEmoji(flow: ExchangeFlow, ownerPublicKey: string, guestPublicKey: string): Promise<readonly Sas[]> {
  return (await sasIndexes(flow, ownerPublicKey, guestPublicKey)).map((index) => SAS_EMOJI[index] ?? { emoji: '❓', name: 'unknown' });
}

const NAME_KEY = 'bridge.nearby.name';

/** The name this tab had, so a reload keeps it. */
export function savedNearbyName(): string | null {
  try {
    return sessionStorage.getItem(NAME_KEY);
  } catch {
    return null;
  }
}

export function saveNearbyName(name: string): void {
  try {
    sessionStorage.setItem(NAME_KEY, name);
  } catch {
    // private mode: a reload just gets a new name
  }
}
